import { FAIcon } from '@/components/fa-icon'
import { useTranslate } from '@ur-solutions/react-hooks'
import { format, isSameDay, parseISO } from 'date-fns'
import { useTheme } from 'styled-components'
import type { ApprovalRestPeriodNode } from '../../types.graphql'
import { DietButton } from '../DietButton'
import { SalaryDayButton } from '../SalaryDayButton'
import {
  formatRestDuration,
  restNights,
  restTypeLabel,
  useRestLabels,
} from './rest-period-format'
import { RestPaidSwitch } from './rest-paid-switch'

/**
 * The card-list counterpart of `RestBandRow`: a slim strip between two shift
 * cards, so the day and week rest filters have something to show on a phone.
 */
export const RestBandCard: React.FC<{
  restPeriod: ApprovalRestPeriodNode
  canChangePaid: boolean
  onChange: () => void
}> = ({ restPeriod, canChangePaid, onChange }) => {
  const labels = useRestLabels()
  const theme = useTheme()
  const start = parseISO(restPeriod.datetimeStart)
  const end = parseISO(restPeriod.datetimeEnd)

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-stone-100 px-4 py-2 font-mono text-xs text-stone-600">
      <FAIcon
        icon="moon"
        type="solid"
        style={{ color: theme.colors.primary500 }}
      />
      <span>
        {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
      </span>
      <span>{formatRestDuration(start, end)}</span>
      <span>{restTypeLabel(restPeriod.restType, labels).toLowerCase()}</span>
      <RestPaidSwitch
        restPeriod={restPeriod}
        canChangePaid={canChangePaid}
        onChange={onChange}
        className="ml-auto"
      />
    </div>
  )
}

interface RestPeriodCardProps {
  restPeriod: ApprovalRestPeriodNode
  userId: string
  canEdit: boolean
  showSalaryMode: boolean
  showDiet: boolean
  showApproval: boolean
  onChange: () => void
}

/**
 * The card-list counterpart of `RestPeriodRow`: a weekly rest carries a diet
 * or salary day, so it gets a card of its own with the same award buttons a
 * shift card has.
 */
export const RestPeriodCard: React.FC<RestPeriodCardProps> = ({
  restPeriod,
  userId,
  canEdit,
  showSalaryMode,
  showDiet,
  showApproval,
  onChange,
}) => {
  const labels = useRestLabels()
  const theme = useTheme()
  const translations = useTranslate({
    approvalFollowsShift: 'salary.rest-approval-follows-shift',
  })
  const start = parseISO(restPeriod.datetimeStart)
  const end = parseISO(restPeriod.datetimeEnd)
  const dateLines = isSameDay(start, end)
    ? [format(start, 'EEE dd. MMMM')]
    : [format(start, 'EEE dd. MMMM'), format(end, 'EEE dd. MMMM')]
  const nights = restNights(start, end)
  const showSalaryDay =
    showSalaryMode && restPeriod.salarySetting?.salaryType === 'DAY'

  return (
    <div className="mb-4 flex flex-col gap-4 rounded-xl bg-stone-100 px-5 py-4">
      <div className="flex items-start gap-3">
        <FAIcon
          icon="moon"
          type="solid"
          className="mt-1"
          style={{ color: theme.colors.primary500 }}
        />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-lg leading-tight font-semibold text-stone-700">
            {restTypeLabel(restPeriod.restType, labels)}
          </span>
          {dateLines.map(dateLine => (
            <span key={dateLine} className="text-sm text-stone-500">
              {dateLine}
            </span>
          ))}
        </div>
        <span className="ml-auto font-mono text-sm text-stone-500">
          {formatRestDuration(start, end)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-stone-200 bg-white p-3 text-2xl leading-none font-semibold text-stone-600">
          {format(start, 'HH:mm')}
        </div>
        <div className="rounded-lg border border-stone-200 bg-white p-3 text-2xl leading-none font-semibold text-stone-600">
          {format(end, 'HH:mm')}
        </div>
      </div>

      {(showSalaryMode || showDiet) && (
        <div className="flex flex-col gap-2">
          {showSalaryMode && restPeriod.salarySetting && (
            <span className="text-sm text-stone-500 italic">
              {restPeriod.salarySetting.name}
            </span>
          )}
          {showSalaryDay && (
            <SalaryDayButton
              restPeriod={{ ...restPeriod, nights }}
              salaryDay={restPeriod.salaryDay}
              userId={userId}
              canEdit={canEdit}
              border
              onChange={onChange}
              restrictWidth
            />
          )}
          {showDiet && (
            <DietButton
              restPeriod={{ ...restPeriod, nights }}
              userId={userId}
              canEdit={canEdit}
              border
              onChange={onChange}
              restrictWidth
            />
          )}
        </div>
      )}

      {showApproval && (
        <span className="flex items-center gap-2 text-sm text-stone-500">
          <FAIcon
            icon={
              restPeriod.workPeriodBefore.approvedByManager
                ? 'circle-check'
                : 'circle-minus'
            }
          />
          {translations.approvalFollowsShift}
        </span>
      )}
    </div>
  )
}
